import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PrivateRoute } from './components/PrivateRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { RoleRouter } from './components/RoleRouter';

// Auth
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { NotFound } from './pages/NotFound';
import { Unauthorized } from './pages/Unauthorized';
import { ProfileSettings } from './pages/ProfileSettings';
import { PublicTracking } from './pages/PublicTracking';
import { HomePage } from './pages/HomePage';
import { LogisticsRwandaPage } from './pages/LogisticsRwandaPage';
import { PricingPage } from './pages/PricingPage';

// Shared
import { CreateShipment } from './pages/CreateShipment';
import { Tracking } from './pages/Tracking';
import { Transporters } from './pages/Transporters';
import { ApiKeys } from './pages/ApiKeys';
import { Webhooks } from './pages/Webhooks';
import { Payments } from './pages/Payments';
import { SharedShipmentDetail } from './pages/shared/SharedShipmentDetail';
import { ShipmentDetailPage } from './pages/ShipmentDetailPage';
import { DeliverShipment } from './pages/shared/DeliverShipment';
import { EditShipment } from './pages/EditShipment';

// Inventory — removed (no warehouse operations)

// Super Admin
import { SuperAdminDashboard } from './pages/super-admin/SuperAdminDashboard';
import { SuperAdminCompanies } from './pages/super-admin/SuperAdminCompanies';
import { SuperAdminCompanyDetail } from './pages/super-admin/SuperAdminCompanyDetail';
import { SuperAdminCompanyForm } from './pages/super-admin/SuperAdminCompanyForm';
import { SuperAdminUsers } from './pages/super-admin/SuperAdminUsers';
import { SuperAdminUserForm } from './pages/super-admin/SuperAdminUserForm';
import { SuperAdminShipments } from './pages/super-admin/SuperAdminShipments';
import { SuperAdminPayments } from './pages/super-admin/SuperAdminPayments';
import { SuperAdminWebhooks } from './pages/super-admin/SuperAdminWebhooks';
import { SuperAdminReports } from './pages/super-admin/SuperAdminReports';
import { SuperAdminAuditLog } from './pages/super-admin/SuperAdminAuditLog';
import { SuperAdminSystemHealth } from './pages/super-admin/SuperAdminSystemHealth';
import { SuperAdminSettings } from './pages/super-admin/SuperAdminSettings';
import { SuperAdminBilling } from './pages/super-admin/SuperAdminBilling';

// Company Admin
import { CompanyAdminDashboard } from './pages/company-admin/CompanyAdminDashboard';
import { CompanyAdminShipments } from './pages/company-admin/CompanyAdminShipments';
import { CompanyAdminBranches } from './pages/company-admin/CompanyAdminBranches';
import { CompanyAdminUsers } from './pages/company-admin/CompanyAdminUsers';
import { CompanyAdminUserForm } from './pages/company-admin/CompanyAdminUserForm';
import { CompanyAdminProfile } from './pages/company-admin/CompanyAdminProfile';
import { CompanyAdminReports } from './pages/company-admin/CompanyAdminReports';
import { CompanyAdminPayments } from './pages/company-admin/CompanyAdminPayments';
import { CompanyAdminAuditLog } from './pages/company-admin/CompanyAdminAuditLog';
import { CompanyAdminApiKeys } from './pages/company-admin/CompanyAdminApiKeys';
import { CompanyAdminWebhooks } from './pages/company-admin/CompanyAdminWebhooks';

// Dispatch Agent
import { DispatchDashboard } from './pages/dispatch/DispatchDashboard';
import { DispatchShipments } from './pages/dispatch/DispatchShipments';
import { DispatchAction } from './pages/dispatch/DispatchAction';
import { DispatchManifest } from './pages/dispatch/DispatchManifest';
import { DispatchExceptions } from './pages/dispatch/DispatchExceptions';
import { DispatchReports } from './pages/dispatch/DispatchReports';

// Recovery Officer
import { RecoveryDashboard } from './pages/recovery/RecoveryDashboard';
import { RecoveryShipments } from './pages/recovery/RecoveryShipments';
import { RecoveryPayments } from './pages/recovery/RecoveryPayments';
import { RecoveryReports } from './pages/recovery/RecoveryReports';

// Company Agent
import { AgentDashboard } from './pages/agent/AgentDashboard';
import { AgentShipments } from './pages/agent/AgentShipments';
import { AgentPayments } from './pages/agent/AgentPayments';
import { AgentReports } from './pages/agent/AgentReports';

// Independent User
import { UserDashboard } from './pages/user/UserDashboard';
import { UserShipments } from './pages/user/UserShipments';
import { UserPayments } from './pages/user/UserPayments';

// Transporter
import { TransporterDashboard } from './pages/transporter/TransporterDashboard';
import { TransporterShipments } from './pages/transporter/TransporterShipments';

import { ToastProvider } from './context/ToastContext';
import './styles/index.css';
import './styles/layout.css';

const ALL_ROLES = ['SUPER_ADMIN', 'COMPANY_ADMIN', 'DISPATCH_AGENT', 'RECOVERY_OFFICER', 'COMPANY_AGENT', 'INDEPENDENT_USER', 'TRANSPORTER'];

const Guard = (roles: string[], children: React.ReactNode) => (
  <PrivateRoute requiredRoles={roles}>
    <Layout>{children}</Layout>
  </PrivateRoute>
);

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/home" element={<HomePage />} />
            <Route path="/logistics-rwanda" element={<LogisticsRwandaPage />} />
            <Route path="/courier-services-rwanda" element={<LogisticsRwandaPage />} />
            <Route path="/shipment-tracking-rwanda" element={<LogisticsRwandaPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/track" element={<PublicTracking />} />
            <Route path="/track/:trackingNumber" element={<PublicTracking />} />
            <Route path="/pricing" element={<PricingPage />} />

            {/* ── Role-scoped profile routes ── */}
            <Route path="/super-admin/profile" element={Guard(['SUPER_ADMIN'], <ProfileSettings />)} />
            <Route path="/company-admin/profile" element={Guard(['COMPANY_ADMIN'], <ProfileSettings />)} />
            <Route path="/dispatch/profile" element={Guard(['DISPATCH_AGENT'], <ProfileSettings />)} />
            <Route path="/recovery/profile" element={Guard(['RECOVERY_OFFICER'], <ProfileSettings />)} />
            <Route path="/agent/profile" element={Guard(['COMPANY_AGENT'], <ProfileSettings />)} />
            <Route path="/user/profile" element={Guard(['INDEPENDENT_USER'], <ProfileSettings />)} />
            <Route path="/transporter/profile" element={Guard(['TRANSPORTER'], <ProfileSettings />)} />

            {/* ── SUPER ADMIN ── */}
            <Route path="/super-admin/dashboard" element={Guard(['SUPER_ADMIN'], <SuperAdminDashboard />)} />
            <Route path="/super-admin/companies" element={Guard(['SUPER_ADMIN'], <SuperAdminCompanies />)} />
            <Route path="/super-admin/companies/new" element={Guard(['SUPER_ADMIN'], <SuperAdminCompanyForm />)} />
            <Route path="/super-admin/companies/:id" element={Guard(['SUPER_ADMIN'], <SuperAdminCompanyDetail />)} />
            <Route path="/super-admin/companies/:id/edit" element={Guard(['SUPER_ADMIN'], <SuperAdminCompanyForm />)} />
            <Route path="/super-admin/users" element={Guard(['SUPER_ADMIN'], <SuperAdminUsers />)} />
            <Route path="/super-admin/users/new" element={Guard(['SUPER_ADMIN'], <SuperAdminUserForm />)} />
            <Route path="/super-admin/users/:id/edit" element={Guard(['SUPER_ADMIN'], <SuperAdminUserForm />)} />
            <Route path="/super-admin/shipments" element={Guard(['SUPER_ADMIN'], <SuperAdminShipments />)} />
            <Route path="/super-admin/shipments/create" element={Guard(['SUPER_ADMIN'], <CreateShipment />)} />
            <Route path="/super-admin/shipments/:id" element={Guard(['SUPER_ADMIN'], <SharedShipmentDetail basePath="/super-admin/shipments" />)} />
            <Route path="/super-admin/shipments/:id/edit" element={Guard(['SUPER_ADMIN'], <EditShipment basePath="/super-admin/shipments" />)} />
            <Route path="/super-admin/shipments/:id/dispatch" element={Guard(['SUPER_ADMIN'], <DispatchAction />)} />
            <Route path="/super-admin/shipments/:id/deliver" element={Guard(['SUPER_ADMIN'], <DeliverShipment basePath="/super-admin/shipments" />)} />
            <Route path="/super-admin/payments" element={Guard(['SUPER_ADMIN'], <SuperAdminPayments />)} />
            <Route path="/super-admin/webhooks" element={Guard(['SUPER_ADMIN'], <SuperAdminWebhooks />)} />
            <Route path="/super-admin/tracking" element={Guard(['SUPER_ADMIN'], <Tracking />)} />
            <Route path="/super-admin/transporters" element={Guard(['SUPER_ADMIN'], <Transporters />)} />
            <Route path="/super-admin/api-keys" element={Guard(['SUPER_ADMIN'], <ApiKeys />)} />
            <Route path="/super-admin/reports" element={Guard(['SUPER_ADMIN'], <SuperAdminReports />)} />
            <Route path="/super-admin/audit-log" element={Guard(['SUPER_ADMIN'], <SuperAdminAuditLog />)} />
            <Route path="/super-admin/system-health" element={Guard(['SUPER_ADMIN'], <SuperAdminSystemHealth />)} />
            <Route path="/super-admin/settings" element={Guard(['SUPER_ADMIN'], <SuperAdminSettings />)} />
            <Route path="/super-admin/billing" element={Guard(['SUPER_ADMIN'], <SuperAdminBilling />)} />

            {/* ── COMPANY ADMIN ── */}
            <Route path="/company-admin/dashboard" element={Guard(['COMPANY_ADMIN'], <CompanyAdminDashboard />)} />
            <Route path="/company-admin/shipments" element={Guard(['COMPANY_ADMIN'], <CompanyAdminShipments />)} />
            <Route path="/company-admin/shipments/create" element={Guard(['COMPANY_ADMIN'], <CreateShipment />)} />
            <Route path="/company-admin/shipments/:id" element={Guard(['COMPANY_ADMIN'], <SharedShipmentDetail basePath="/company-admin/shipments" />)} />
            <Route path="/company-admin/shipments/:id/edit" element={Guard(['COMPANY_ADMIN'], <EditShipment basePath="/company-admin/shipments" />)} />
            <Route path="/company-admin/shipments/:id/deliver" element={Guard(['COMPANY_ADMIN'], <DeliverShipment basePath="/company-admin/shipments" />)} />
            <Route path="/company-admin/payments" element={Guard(['COMPANY_ADMIN'], <CompanyAdminPayments />)} />
            <Route path="/company-admin/branches" element={Guard(['COMPANY_ADMIN'], <CompanyAdminBranches />)} />
            <Route path="/company-admin/users" element={Guard(['COMPANY_ADMIN'], <CompanyAdminUsers />)} />
            <Route path="/company-admin/users/new" element={Guard(['COMPANY_ADMIN'], <CompanyAdminUserForm />)} />
            <Route path="/company-admin/users/:id/edit" element={Guard(['COMPANY_ADMIN'], <CompanyAdminUserForm />)} />
            <Route path="/company-admin/tracking" element={Guard(['COMPANY_ADMIN'], <Tracking />)} />
            <Route path="/company-admin/api-keys" element={Guard(['COMPANY_ADMIN'], <CompanyAdminApiKeys />)} />
            <Route path="/company-admin/webhooks" element={Guard(['COMPANY_ADMIN'], <CompanyAdminWebhooks />)} />
            <Route path="/company-admin/company-profile" element={Guard(['COMPANY_ADMIN'], <CompanyAdminProfile />)} />
            <Route path="/company-admin/reports" element={Guard(['COMPANY_ADMIN'], <CompanyAdminReports />)} />
            <Route path="/company-admin/audit-log" element={Guard(['COMPANY_ADMIN'], <CompanyAdminAuditLog />)} />

            {/* ── DISPATCH AGENT (E.H.C internal — sees all companies) ── */}
            <Route path="/dispatch/dashboard" element={Guard(['DISPATCH_AGENT'], <DispatchDashboard />)} />
            <Route path="/dispatch/shipments" element={Guard(['DISPATCH_AGENT'], <DispatchShipments />)} />
            <Route path="/dispatch/shipments/:id" element={Guard(['DISPATCH_AGENT'], <SharedShipmentDetail basePath="/dispatch/shipments" />)} />
            <Route path="/dispatch/shipments/:id/dispatch" element={Guard(['DISPATCH_AGENT'], <DispatchAction />)} />
            <Route path="/dispatch/shipments/:id/deliver" element={Guard(['DISPATCH_AGENT'], <DeliverShipment basePath="/dispatch/shipments" />)} />
            <Route path="/dispatch/payments" element={Guard(['DISPATCH_AGENT'], <Payments />)} />
            <Route path="/dispatch/transporters" element={Guard(['DISPATCH_AGENT'], <Transporters />)} />
            <Route path="/dispatch/tracking" element={Guard(['DISPATCH_AGENT'], <Tracking />)} />
            <Route path="/dispatch/manifest" element={Guard(['DISPATCH_AGENT'], <DispatchManifest />)} />
            <Route path="/dispatch/exceptions" element={Guard(['DISPATCH_AGENT'], <DispatchExceptions />)} />
            <Route path="/dispatch/reports" element={Guard(['DISPATCH_AGENT'], <DispatchReports />)} />

            {/* ── RECOVERY OFFICER ── */}
            <Route path="/recovery/dashboard" element={Guard(['RECOVERY_OFFICER'], <RecoveryDashboard />)} />
            <Route path="/recovery/shipments" element={Guard(['RECOVERY_OFFICER'], <RecoveryShipments />)} />
            <Route path="/recovery/shipments/:id" element={Guard(['RECOVERY_OFFICER'], <SharedShipmentDetail basePath="/recovery/shipments" />)} />
            <Route path="/recovery/shipments/:id/deliver" element={Guard(['RECOVERY_OFFICER'], <DeliverShipment basePath="/recovery/shipments" />)} />
            <Route path="/recovery/payments" element={Guard(['RECOVERY_OFFICER'], <RecoveryPayments />)} />
            <Route path="/recovery/tracking" element={Guard(['RECOVERY_OFFICER'], <Tracking />)} />
            <Route path="/recovery/reports" element={Guard(['RECOVERY_OFFICER'], <RecoveryReports />)} />

            {/* ── COMPANY AGENT ── */}
            <Route path="/agent/dashboard" element={Guard(['COMPANY_AGENT'], <AgentDashboard />)} />
            <Route path="/agent/shipments" element={Guard(['COMPANY_AGENT'], <AgentShipments />)} />
            <Route path="/agent/shipments/create" element={Guard(['COMPANY_AGENT'], <CreateShipment />)} />
            <Route path="/agent/shipments/:id" element={Guard(['COMPANY_AGENT'], <SharedShipmentDetail basePath="/agent/shipments" />)} />
            <Route path="/agent/shipments/:id/edit" element={Guard(['COMPANY_AGENT'], <EditShipment basePath="/agent/shipments" />)} />
            <Route path="/agent/shipments/:id/deliver" element={Guard(['COMPANY_AGENT'], <DeliverShipment basePath="/agent/shipments" />)} />
            <Route path="/agent/payments" element={Guard(['COMPANY_AGENT'], <AgentPayments />)} />
            <Route path="/agent/reports" element={Guard(['COMPANY_AGENT'], <AgentReports />)} />
            <Route path="/agent/tracking" element={Guard(['COMPANY_AGENT'], <Tracking />)} />

            {/* ── INDEPENDENT USER ── */}
            <Route path="/user/dashboard" element={Guard(['INDEPENDENT_USER'], <UserDashboard />)} />
            <Route path="/user/shipments" element={Guard(['INDEPENDENT_USER'], <UserShipments />)} />
            <Route path="/user/shipments/create" element={Guard(['INDEPENDENT_USER'], <CreateShipment />)} />
            <Route path="/user/shipments/:id" element={Guard(['INDEPENDENT_USER'], <SharedShipmentDetail basePath="/user/shipments" />)} />
            <Route path="/user/shipments/:id/edit" element={Guard(['INDEPENDENT_USER'], <EditShipment basePath="/user/shipments" />)} />
            <Route path="/user/payments" element={Guard(['INDEPENDENT_USER'], <UserPayments />)} />
            <Route path="/user/tracking" element={Guard(['INDEPENDENT_USER'], <Tracking />)} />

            {/* ── TRANSPORTER ── */}
            <Route path="/transporter/dashboard" element={Guard(['TRANSPORTER'], <TransporterDashboard />)} />
            <Route path="/transporter/shipments" element={Guard(['TRANSPORTER'], <TransporterShipments />)} />
            <Route path="/transporter/shipments/:id" element={Guard(['TRANSPORTER'], <SharedShipmentDetail basePath="/transporter/shipments" />)} />
            <Route path="/transporter/tracking" element={Guard(['TRANSPORTER'], <Tracking />)} />

{/* ── Universal shipment detail (all authenticated roles) ── */}
            <Route path="/shipments/:id" element={Guard(ALL_ROLES, <ShipmentDetailPage />)} />

            {/* Root redirect → role-based home */}
            <Route path="/" element={<RoleRouter />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
